import { Title, Meta } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { Configuration } from '../constant/configuration';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

type ITitleMetaTag = { title: string };
type ITitleTranslationMetaTag = {
    titleTranslationKey: string;
    titleInterpolateParams?: { [key: string]: string };
};

type IMetaTag = (ITitleMetaTag | ITitleTranslationMetaTag) & {
    descriptionTranslationKey?: string;
    descriptionInterpolateParams?: { [key: string]: string };
    description?: string;
    imageUrl?: string;
    magazineUrl?: string;
};

@Injectable({ providedIn: 'root' })
export class SeoTagsService {
    lastUrlDefinedSeo: IMetaTag & { url: string };
    isSEODefinedByComponent = false;
    constructor(
        private translateService: TranslateService,
        private configuration: Configuration,
        private title: Title,
        private meta: Meta,
        private router: Router
    ) {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                const lastUrl = this.lastUrlDefinedSeo?.url.split('?')[0];
                const newUrlToNavigate = event.url.split('?')[0];

                if (this.lastUrlDefinedSeo && !newUrlToNavigate.startsWith(lastUrl)) {
                    this.isSEODefinedByComponent = false;
                    this.lastUrlDefinedSeo = null;
                }
            }

            if (event instanceof NavigationEnd) {
                if (!this.isSEODefinedByComponent) {
                    this.defineDefaultMetaTags();
                }
                this.checkAndUpdateCanonical(event.urlAfterRedirects);
            }
        });
    }

    initialize(): void {
        this.defineDefaultMetaTags();

        this.translateService.onLangChange.subscribe(() => {
            if (this.lastUrlDefinedSeo) {
                this.updateSEOTags(this.lastUrlDefinedSeo);
            }
        });
    }

    defineDefaultMetaTags(): void {
        this.updateSEOTags(
            {
                titleTranslationKey: 'global.title',
                descriptionTranslationKey: 'global.description'
            },
            false
        );
    }

    updateSEOTags(payload: IMetaTag, isSEODefinedByComponent = true): void {
        this.isSEODefinedByComponent = isSEODefinedByComponent;
        this.lastUrlDefinedSeo = { ...payload, url: this.router.url };

        const title = this.generateTitleFromMetaTag(payload);
        const description = this.generateDescriptionFromMetaTag(payload);
        const magazineUrl = this.lastUrlDefinedSeo.magazineUrl;

        this.title.setTitle(title);
        this.meta.updateTag({ property: 'og:title', content: title });
        this.meta.updateTag({ name: 'description', content: description });
        this.meta.updateTag({ property: 'og:description ', content: description });

        if (payload.imageUrl) {
            this.meta.updateTag({ property: 'og:image', content: payload.imageUrl });
        } else {
            this.meta.updateTag({ property: 'og:image', content: 'images/logo/lexnow_big.png' });
        }

        // Set canonical link
        if (magazineUrl) {
            // Set canonical link for current magazine from magazineUrl
            this.updateCanonicalUrl(magazineUrl);
        } else {
            const fullDomain = window.location.origin;
            const url = this.router.url;
            // Set canonical link for current magazine as current url
            this.updateCanonicalUrl(fullDomain + url);
        }
    }

    generateTitleFromMetaTag(payload: IMetaTag): string {
        if ((payload as ITitleMetaTag).title) {
            return `${this.configuration.appName} | ${(payload as ITitleMetaTag).title}`;
        } else {
            const titleData = payload as ITitleTranslationMetaTag;
            return `${this.configuration.appName} |
              ${
                  this.translateService.instant(
                      titleData.titleTranslationKey,
                      titleData.titleInterpolateParams || {}
                  ) as string
              }`;
        }
    }

    generateDescriptionFromMetaTag(payload: IMetaTag): string {
        let description = '';
        if (payload.description) {
            description = payload.description;
        } else if (payload.descriptionTranslationKey) {
            description = this.translateService.instant(
                payload.descriptionTranslationKey,
                payload.descriptionInterpolateParams || {}
            ) as string;
        }

        return description;
    }

    checkAndUpdateCanonical(url: string): void {
        const fullDomain = window.location.origin;
        if (
            url.includes('/public/library') ||
            url.includes('/lexique') ||
            url.includes('/glossary') ||
            url.includes('/lexicon')
        ) {
            this.updateCanonicalUrl(fullDomain + url);
        } else if (!url.includes('/public/magazine')) {
            // Place general canonical (e.g. 'https://app.lexnow.io') for all internal pages protected by login including /login page itself
            this.updateCanonicalUrl(fullDomain);
        }
    }

    updateCanonicalUrl(canonicalUrl: string): void {
        let link: HTMLLinkElement = document.querySelector("link[rel='canonical']");
        if (link) {
            link.setAttribute('href', canonicalUrl);
        } else {
            link = document.createElement('link');
            link.setAttribute('rel', 'canonical');
            link.setAttribute('href', canonicalUrl);
            document.head.appendChild(link);
        }
    }
}
